import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { PageShell, Section } from "@/components/PageShell";
import { SeoCta, SeoFaq, SeoRelated } from "@/components/SeoBits";
import { SEO_FAQS } from "@/lib/seoPages";

const a = "font-semibold text-[#7FC8A9] underline decoration-[#7FC8A9]/40 underline-offset-2 hover:text-white";

const INCLUDED = [
  "The Notion Command Center, the planning system that works on the free Notion plan in any browser",
  "The Today web app at commandcenteradhd.com/today, with a daily view, quick wins, honest time blocks, and Low Battery Mode",
  "Future updates to the system, included at no extra cost",
  "A 30 day money back guarantee",
];

export default function AdhdPlannerNoSubscription() {
  return (
    <PageShell
      testid="page-adhd-planner-no-subscription"
      title="An ADHD planner with no subscription"
      subtitle="You buy it once for $17. No subscription, no recurring fees, and future updates are included."
    >
      <p>
        An ADHD planner with no subscription is a planning system you buy once and keep, with no recurring fee and
        no account to cancel later. Subscriptions can be quietly hard to keep up with when you have ADHD. Another
        monthly charge, another login, another thing to cancel later and forget to. CommandCenterADHD takes a
        different path. You buy it once for $17 and it is yours.
      </p>

      <Section heading="Why subscriptions are hard to keep up with">
        <p>
          A recurring charge asks you to keep deciding, month after month, whether a tool is worth it. That is a
          small tax on attention, and small taxes add up. It is also easy to end up paying for something you
          stopped opening weeks ago. A one time purchase removes that background worry. You decide once, and then
          you can just use it.
        </p>
        <p>
          There is a quieter cost too. Every active subscription is one more login to manage, one more renewal to
          track, and one more thing to cancel later. For a lot of people with ADHD, that ongoing admin is exactly
          the kind of friction that makes a useful tool slip away.
        </p>
      </Section>

      <Section heading="Who a no subscription planner helps">
        <p>
          A one time purchase suits anyone who has quietly canceled a stack of subscriptions, or who feels a small
          dread each time a renewal lands. It also suits people who want to try a system without signing up for an
          ongoing commitment. You pay once, you keep it, and there is nothing to remember to cancel later.
        </p>
      </Section>

      <Section heading="What you get for $17, one time">
        <p>Your purchase includes two parts and a couple of promises.</p>
        <ul className="space-y-2.5" data-testid="seo-included-list">
          {INCLUDED.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-[#15816B] text-white">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <span className="text-[15px] leading-relaxed text-[#BFD4E8]">{item}</span>
            </li>
          ))}
        </ul>
        <p>
          You can see how the daily flow works on the{" "}
          <Link to="/adhd-daily-planner" className={a}>daily planner page</Link>.
        </p>
      </Section>

      <Section heading="Runs on the free Notion plan">
        <p>
          The Command Center is built on Notion and works on the free Notion plan in any browser. You do not need
          a paid Notion account and you do not need to install anything special. If you already use Notion, it
          drops into your space. If you do not, you can start on the free plan.
        </p>
      </Section>

      <Section heading="The daily view and Low Battery Mode">
        <p>
          Beyond the planning system, you get the Today web app, a focused daily view designed for one screen. On
          a normal day it holds your priority, quick wins, and time blocks. On a hard day, Low Battery Mode shrinks
          everything to one must do, one quick win, and a short focus timer, with a calm Can wait note for the
          rest. There is more on how that helps on the{" "}
          <Link to="/adhd-overwhelm" className={a}>ADHD overwhelm page</Link>.
        </p>
      </Section>

      <Section heading="Future updates included">
        <p>
          As the system improves, you get the updates at no extra cost. There is no upgrade tier and no premium
          plan waiting behind a second paywall. The $17 you pay is the whole price.
        </p>
        <p>
          Because there is no subscription, the cost does not change with time or usage. You will not be moved onto
          a higher tier, and a feature you rely on will not slip behind a new charge later. The one time price is
          the whole relationship.
        </p>
      </Section>

      <Section heading="What happens after you buy">
        <p>
          After checkout you get access to both parts. You duplicate the Notion Command Center into your own space
          on the free Notion plan, and you sign in to the Today web app at commandcenteradhd.com/today with the
          email you used at checkout. There is no separate account to create and no card kept on file for a future
          charge.
        </p>
      </Section>

      <Section heading="A simple, low risk way to try it">
        <p>
          If you would rather see the structure first, the{" "}
          <Link to="/free-planner" className={a}>free printable planner</Link> gives you the daily layout on paper
          at no cost. You can also try the free{" "}
          <Link to="/what-should-i-do-next" className={a}>What should I do next tool</Link> to feel the one thing
          at a time approach. When you are ready for the full system, the Command Center is a one time $17 purchase
          with a 30 day money back guarantee.
        </p>
        <SeoCta slug="adhd-planner-no-subscription" />
      </Section>

      <Section heading="Frequently asked questions">
        <SeoFaq faqs={SEO_FAQS["/adhd-planner-no-subscription"]} />
      </Section>

      <SeoRelated
        links={[
          { to: "/adhd-daily-planner", label: "An ADHD daily planner built for real days" },
          { to: "/adhd-overwhelm", label: "When ADHD overwhelm takes over, start here" },
          { to: "/guides", label: "Setup guides for the Command Center" },
        ]}
      />
    </PageShell>
  );
}
